/**
 * The exposure grid — one `<tbody>` per event, one row per market, runner
 * positions in fixed columns that line up across every event on the page.
 *
 * The screen this replaces put a repeated `1 / X / 2` header strip inside every
 * event group and never once said WHICH side was 1 and which was 2, so a number
 * in the "2" column could not be read without opening the market. Here the
 * column header strip is sticky and lives once, and the group row spells out
 * the runner names it is about to print numbers under.
 *
 * No data fetching, no filter state: it renders the events it is given.
 */

import { useMemo, useState, type MouseEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Card, Chip, Dot, ExposureCell, ExposurePill, ScrollRegion } from '@w7/ui'
import { count, lll, money, toneOf } from '@/lib/format'
import { shapeEvent, worstFirst, worstRunner, type RunnerCell, type ShapedMarket } from './runners'
import type { EventExposure } from '@/data/types'
import './exposure-grid.css'

/**
 * Bare, rounded, signed — the ₹ is carried by the column header and the exact
 * figure lives in the cell's `title`. Paise in a 200-row grid are noise; paise
 * in a tooltip are provenance.
 */
const grid = (value: number) => money(value, { symbol: false, round: true, signed: true })
const flat = (value: number) => money(value, { symbol: false, round: true })

/**
 * A screen-reader user must never hear a bare "minus thirty-six thousand" under
 * a column called "2". Every money cell in the grid names its runner and says
 * which way the money went, so tone is never carried by colour alone.
 */
const srBook = (runner: RunnerCell) =>
  runner.net === 0
    ? `${runner.name} — the book is level`
    : `${runner.name} — the book ${runner.net < 0 ? 'pays' : 'keeps'} ${money(Math.abs(runner.net))}`

/** Column widths and the hide-below steps come from `.w7-expo` in the kit. */
const HIDE = { best: 1080, matched: 1280, counts: 900 } as const
/** r1 + rx + r2 — what a field market takes instead of three numbered slots. */
const RUNNER_SPAN = 3
/** Every column except the sticky market cell. */
const REST_SPAN = 7

/**
 * Eight columns, and deliberately NO row total.
 *
 * The runner cells on a row are mutually exclusive outcomes — three different
 * futures, only one of which happens — so a sum across them is a number that
 * can never occur. Worst case is `min(runners)`, never a row sum, and the
 * absence of a total column is what stops anyone reaching for one.
 */
const HEADERS: { col: string; label: string; help?: string; hideBelow?: number }[] = [
  { col: 'market', label: 'Event / market' },
  {
    col: 'worst',
    label: 'Worst case ₹',
    help: "The smallest of this row's outcomes — what the book pays if the worst runner wins.",
  },
  {
    col: 'best',
    label: 'Best case ₹',
    help: "The largest outcome — what the book keeps if the best runner wins.",
    hideBelow: HIDE.best,
  },
  { col: 'r1', label: '1', help: 'Home side — the first name in the event' },
  { col: 'rx', label: 'X', help: 'The draw, where the market has one' },
  { col: 'r2', label: '2', help: 'Away side' },
  { col: 'matched', label: 'Matched ₹', hideBelow: HIDE.matched },
  { col: 'counts', label: 'Bets / members', hideBelow: HIDE.counts },
]

export interface ExposureGridProps {
  events: EventExposure[]
  density: 'compact' | 'default'
  /** Names the active ordering in the table caption. */
  sortLabel: string
}

export function ExposureGrid({ events, density, sortLabel }: ExposureGridProps) {
  /* Which field markets have their full runner list open. Component-local: it
     is a disclosure, not a filter, so it neither belongs in the URL nor should
     survive a shared link. */
  const [open, setOpen] = useState<Set<string>>(() => new Set())
  const toggle = (marketId: string) =>
    setOpen((current) => {
      const next = new Set(current)
      if (!next.delete(marketId)) next.add(marketId)
      return next
    })

  return (
    <Card flush>
      {/*
        The pane is height-bounded on purpose. `position: sticky` resolves
        against the nearest SCROLLING ancestor, so an unbounded `overflow: auto`
        wrapper scrolls sideways but never vertically — and the sticky column
        header and sticky event rows would simply never engage during page
        scroll. Bounding it makes the grid an internally-scrolling pane where
        the header, the event you are reading and the market-name column all
        stay put through fifty events. `dvh` not `vh`, so a phone's collapsing
        URL bar does not leave the grid short.

        There is deliberately NO PAGINATION. This is a live book, and a page 2
        that hides ₹4 lakh of liability is worse than a long scroll. The filters
        and the sort are the answer to length.
      */}
      <ScrollRegion
        label="Exposure by event and market — scrolls sideways on a narrow screen"
      >
        <table className="w7-table w7-expo" data-density={density}>
          <caption className="w7-sr">
            Open positions by event and market, {sortLabel.toLowerCase()}
          </caption>
          <thead>
            <tr>
              {HEADERS.map((header) => (
                <th
                  key={header.col}
                  data-col={header.col}
                  data-align={header.col === 'market' ? undefined : 'right'}
                  data-hide-below={header.hideBelow}
                  title={header.help}
                  scope="col"
                >
                  {header.label}
                </th>
              ))}
            </tr>
          </thead>

          {events.map((event) => (
            <EventGroup key={event.eventId} event={event} open={open} onToggle={toggle} />
          ))}
        </table>
      </ScrollRegion>
    </Card>
  )
}

/* ------------------------------------------------------------ event group -- */

function EventGroup({
  event,
  open,
  onToggle,
}: {
  event: EventExposure
  open: Set<string>
  onToggle: (marketId: string) => void
}) {
  const shaped = useMemo(() => shapeEvent(event), [event])
  const matched = event.markets.reduce((sum, market) => sum + market.matched, 0)

  return (
    <tbody>
      <tr className="w7-expo-group">
        <th scope="rowgroup" data-col="market">
          {/* Two lines, not one. Measured at 1280px the market column gives the
              identity 246px, and an inline `starts …` suffix beside the sport
              and Live chips crushed the event name to 0–27px — the one string
              on the row you cannot do without. The chips and the start time
              have fixed widths; only the name can give, so only the name was
              giving. Stacking them lets the name own a whole line. */}
          <span className="w7-expo-eventcell">
            <span className="w7-expo-eventtop">
              <span className="w7-expo-eventname w7-truncate" title={event.eventName}>
                {event.eventName}
              </span>
              <Chip size="sm" icon="sport">
                {event.sport}
              </Chip>
              {event.inPlay ? (
                // In-play is brand orange, never red: on this console red means
                // the book paid out, and a live badge is not a loss.
                <Chip size="sm" tone="brand">
                  <Dot tone="brand" pulse />
                  Live
                </Chip>
              ) : null}
            </span>
            <span className="w7-expo-starts" data-hide-below={HIDE.counts}>
              {event.inPlay ? 'started' : 'starts'} {lll(event.startsAt)}
            </span>
          </span>
        </th>

        <ExposureCell
          col="worst"
          strong
          value={grid(event.worstCase)}
          tone={toneOf(event.worstCase)}
          title={money(event.worstCase)}
          srText={`Event worst case ${money(event.worstCase)}`}
        />
        <ExposureCell
          col="best"
          strong
          hideBelow={HIDE.best}
          value={grid(event.bestCase)}
          tone={toneOf(event.bestCase)}
          title={money(event.bestCase)}
        />

        {/* The runner names the numbers below are about to be printed under.
            Plain `<td>`, not `<th>`: they are a visual convenience, and the
            authoritative per-runner label travels with each data cell's own
            sr-only sentence. */}
        <RunnerHead col="r1" name={shaped.columns.home} />
        <RunnerHead col="rx" name={shaped.columns.draw} />
        <RunnerHead col="r2" name={shaped.columns.away} />

        <ExposureCell
          col="matched"
          strong
          hideBelow={HIDE.matched}
          value={flat(matched)}
          title={money(matched)}
        />
        <td data-col="counts" data-align="right" data-hide-below={HIDE.counts}>
          <span className="w7-mono">
            {count(event.betCount)} / {count(event.memberCount)}
          </span>
          <span className="w7-sr">
            {' '}
            open bets and {event.memberCount === 1 ? 'member' : 'members'} with a position
          </span>
        </td>
      </tr>

      {shaped.rows.map((row) => (
        <MarketRow
          key={row.market.marketId}
          event={event}
          row={row}
          open={open.has(row.market.marketId)}
          onToggle={onToggle}
        />
      ))}

      {shaped.lines.length > 0 ? (
        <FancyBlock lines={shaped.lines} worst={shaped.fancyWorst} />
      ) : null}
    </tbody>
  )
}

function RunnerHead({ col, name }: { col: string; name: string | null }) {
  return (
    <td data-col={col} data-align="right">
      {name ? (
        <span className="w7-expo-runnerhead" title={name}>
          {name}
        </span>
      ) : null}
    </td>
  )
}

/* ------------------------------------------------------------- market row -- */

function MarketRow({
  event,
  row,
  open,
  onToggle,
}: {
  event: EventExposure
  row: ShapedMarket
  open: boolean
  onToggle: (marketId: string) => void
}) {
  const navigate = useNavigate()
  const { market, shape } = row
  const to = `/exposure/market/${market.marketId}`

  // Mouse convenience only. The link inside the market cell is the row's
  // accessible name and its keyboard entry point, so the handler stands down
  // whenever the click already landed on something interactive — otherwise a
  // click on the link navigates twice.
  const onRowClick = (clicked: MouseEvent<HTMLTableRowElement>) => {
    if ((clicked.target as HTMLElement).closest('a, button')) return
    navigate(to)
  }

  const runners =
    shape.kind === 'sided' ? [shape.home, shape.draw, shape.away] : shape.kind === 'field' ? shape.runners : []
  const worst = worstRunner(runners)

  return (
    <>
      <tr className="w7-expo-row" data-clickable="true" onClick={onRowClick}>
        <td data-col="market">
          <span className="w7-expo-marketcell">
            <Link to={to} className="w7-link w7-truncate" title={market.marketName}>
              {market.marketName}
            </Link>
            {/* Most markets are named after their type; repeating it as a chip
                is noise, so it only appears when it adds something. */}
            {market.marketType === market.marketName ? null : (
              <Chip size="sm">{market.marketType}</Chip>
            )}
          </span>
        </td>

        <ExposureCell
          col="worst"
          value={grid(market.worstCase)}
          tone={toneOf(market.worstCase)}
          title={money(market.worstCase)}
        />
        <ExposureCell
          col="best"
          hideBelow={HIDE.best}
          value={grid(market.bestCase)}
          tone={toneOf(market.bestCase)}
          title={money(market.bestCase)}
        />

        {shape.kind === 'sided' ? (
          <>
            <RunnerSlot col="r1" runner={shape.home} worst={worst} absentLabel="no home side in this market" />
            <RunnerSlot col="rx" runner={shape.draw} worst={worst} absentLabel="no draw in this market" />
            <RunnerSlot col="r2" runner={shape.away} worst={worst} absentLabel="no away side in this market" />
          </>
        ) : (
          <FieldSlot
            event={event}
            row={row}
            runners={shape.kind === 'field' ? shape.runners : []}
            worst={worst}
            open={open}
            onToggle={onToggle}
          />
        )}

        <ExposureCell
          col="matched"
          hideBelow={HIDE.matched}
          value={flat(market.matched)}
          title={`${money(market.matched)} matched, ${money(market.unmatched)} unmatched`}
        />
        <td data-col="counts" data-align="right" data-hide-below={HIDE.counts}>
          <span className="w7-mono">
            {count(market.betCount)} / {count(market.memberCount)}
          </span>
          <span className="w7-sr">
            {' '}
            {market.betCount === 1 ? 'bet' : 'bets'} from{' '}
            {market.memberCount === 1 ? 'one member' : `${count(market.memberCount)} members`}
          </span>
        </td>
      </tr>

      {open && shape.kind === 'field' ? (
        <tr className="w7-expo-fieldmore">
          <td data-col="market" />
          <td colSpan={REST_SPAN} className="w7-expo-wrapcell">
            <span className="w7-expo-field">
              {worstFirst(shape.runners).map((runner) => (
                <RunnerPill key={runner.name} runner={runner} />
              ))}
            </span>
          </td>
        </tr>
      ) : null}
    </>
  )
}

/** One numbered slot. */
function RunnerSlot({
  col,
  runner,
  worst,
  absentLabel,
}: {
  col: string
  runner: RunnerCell | null
  worst: RunnerCell | null
  absentLabel: string
}) {
  // "This market has no such runner" and "the book is flat on this runner" are
  // DIFFERENT FACTS and must not look alike — the original printed a dash for
  // both, so a two-runner market and a market you were square on were
  // indistinguishable at a glance.
  if (!runner) return <ExposureCell col={col} absent absentLabel={absentLabel} />
  return (
    <ExposureCell
      col={col}
      value={grid(runner.net)}
      tone={toneOf(runner.net)}
      worst={runner === worst}
      title={`${runner.name} · ${money(runner.net)}`}
      srText={srBook(runner)}
    />
  )
}

/**
 * Four or more runners — or any market whose sides could not be proven — get a
 * named strip across the three numbered slots instead of a numbered cell. A
 * horse's book never appears under a column headed "1", because "1" means the
 * home side and a seven-runner handicap does not have one.
 */
function FieldSlot({
  event,
  row,
  runners,
  worst,
  open,
  onToggle,
}: {
  event: EventExposure
  row: ShapedMarket
  runners: RunnerCell[]
  worst: RunnerCell | null
  open: boolean
  onToggle: (marketId: string) => void
}) {
  const ranked = worstFirst(runners)
  const shown = ranked.slice(0, 3)
  const hidden = ranked.length - shown.length
  const label = `${open ? 'Hide the full runner list for' : `Show all ${ranked.length} runners in`} ${event.eventName} — ${row.market.marketName}`

  return (
    <td
      className="w7-expo-cell w7-expo-wrapcell"
      colSpan={RUNNER_SPAN}
      // `runners`, not `r1`: a width declaration on a spanning cell fights the
      // three real columns for the same space, and the numbered slots have to
      // stay exactly as wide here as they are in the sided rows above.
      data-col="runners"
      // The strip is one cell, so the marker flags the ROW as one that pays;
      // the pills are worst-first, so the runner it pays on is the first one.
      data-worst={worst ? 'true' : undefined}
    >
      <span className="w7-expo-field">
        {shown.map((runner) => (
          <RunnerPill key={runner.name} runner={runner} />
        ))}
        {hidden > 0 || open ? (
          <button
            type="button"
            className="w7-expo-more"
            aria-expanded={open}
            aria-label={label}
            onClick={() => onToggle(row.market.marketId)}
          >
            {open ? 'Show fewer' : `+${hidden} more`}
          </button>
        ) : null}
      </span>
    </td>
  )
}

function RunnerPill({ runner }: { runner: RunnerCell }) {
  return (
    <span title={`${runner.name} · ${money(runner.net)}`}>
      <ExposurePill label={runner.name} value={grid(runner.net)} tone={toneOf(runner.net)} />
      <span className="w7-sr">{srBook(runner)}</span>
    </span>
  )
}

/* ------------------------------------------------------------ fancy block -- */

/**
 * Every fancy/session line of an event, as wrapped pills on one row.
 *
 * The original gave each line a full-width row that filled two of its eight
 * columns — the Manchester Super Giants W fixture spent eight rows showing
 * eight numbers. Wrapped pills put the same eight numbers in two.
 */
function FancyBlock({ lines, worst }: { lines: ShapedMarket[]; worst: number }) {
  return (
    <tr className="w7-expo-fancy">
      <td data-col="market">
        <span className="w7-expo-fancylabel">
          Fancy &amp; session
          <span className="w7-dimmer"> · {count(lines.length)}</span>
        </span>
      </td>
      <td colSpan={REST_SPAN} className="w7-expo-wrapcell">
        <span className="w7-expo-field">
          <span
            className={`w7-expo-fancytotal w7-mono w7-tone-${toneOf(worst)}`}
            title={`Worst case across ${count(lines.length)} fancy lines: ${money(worst)}. Lines settle independently so their worst cases add; a line that cannot cost you contributes zero, so this never reads better than reality.`}
          >
            {grid(worst)}
          </span>
          {lines.map(({ market }) => (
            <FancyPill
              key={market.marketId}
              to={`/exposure/market/${market.marketId}`}
              name={market.marketName}
              net={market.worstCase}
            />
          ))}
        </span>
      </td>
    </tr>
  )
}

function FancyPill({ to, name, net }: { to: string; name: string; net: number }) {
  return (
    <Link
      to={to}
      className="w7-expo-fancypill"
      data-tone={toneOf(net)}
      title={`${name} · worst case ${money(net)}`}
    >
      <span className="w7-truncate">{name}</span>
      <span className="w7-mono">{grid(net)}</span>
    </Link>
  )
}
